import React, { useState, useMemo, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  Phone,
  Mail,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users,
  Activity,
  TrendingDown,
  AlertTriangle,
  Clock,
  BookOpen,
  Banknote,
  RefreshCw,
  Undo2,
  Contact,
  ExternalLink,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import { Spinner } from '../components/common/Spinner';
import StatCard from '../components/common/StatCard';
import { Pagination } from '../components/common/Pagination';
import { useContactActivity } from '../hooks/useContactActivity';
import type { ContactActivityEntry, FeedSourcePage } from '../hooks/useContactActivity';
import { CONTACT_OUTCOMES } from '../hooks/useLoanContacts';
import type { ContactChannel } from '../hooks/useLoanContacts';

interface OutletContextType {
  userData: any;
  companyData: any;
}

type ViewMode = 'day' | 'week' | 'month';
type SortField = 'createdAt' | 'createdBy' | 'broker' | 'customerName' | 'sourcePage';
type SortDirection = 'asc' | 'desc';
type ChannelFilter = 'all' | ContactChannel;

// ---------------------------------------------------------------------------
// Single source-config registry — add a source here and the chip, stat card,
// filter, deep-link route + button all pick it up (no scattered maps).
// ---------------------------------------------------------------------------
interface SourceConfig {
  badge: string; // chip classes in the table
  button: string; // deep-link button classes
  buttonLabel: string;
  route: string;
  statColor: string; // hex for the monthly stat card
  icon: React.ReactNode;
}

const SOURCE_CONFIG: Record<FeedSourcePage, SourceConfig> = {
  Discharges: {
    badge: 'bg-red-500/20 text-red-400 border-red-500/30',
    button: 'bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25 hover:border-red-500/60',
    buttonLabel: 'VIEW IN DISCHARGED LOANS',
    route: '/reports/discharged-loans',
    statColor: '#ef4444',
    icon: <Banknote className="w-5 h-5" />,
  },
  'Refinance Intelligence': {
    badge: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    button: 'bg-violet-500/15 border-violet-500/40 text-violet-400 hover:bg-violet-500/25 hover:border-violet-500/60',
    buttonLabel: 'VIEW IN CLIENT RETENTION',
    route: '/reports/refinance-intelligence',
    statColor: '#8b5cf6',
    icon: <RefreshCw className="w-5 h-5" />,
  },
  'Acquired Books': {
    badge: 'bg-green-500/20 text-green-400 border-green-500/30',
    button: 'bg-green-500/15 border-green-500/40 text-green-400 hover:bg-green-500/25 hover:border-green-500/60',
    buttonLabel: 'VIEW IN ACQUIRED BOOKS',
    route: '/reports/acquired-books',
    statColor: '#22c55e',
    icon: <BookOpen className="w-5 h-5" />,
  },
  'Settlement Age': {
    badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    button: 'bg-blue-500/15 border-blue-500/40 text-blue-400 hover:bg-blue-500/25 hover:border-blue-500/60',
    buttonLabel: 'VIEW IN SETTLEMENT AGE',
    route: '/reports/settlement-age',
    statColor: '#3b82f6',
    icon: <Clock className="w-5 h-5" />,
  },
  'Trail Drop-Off': {
    badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    button: 'bg-amber-500/15 border-amber-500/40 text-amber-400 hover:bg-amber-500/25 hover:border-amber-500/60',
    buttonLabel: 'VIEW IN TRAIL DROP-OFF',
    route: '/reports/trail-drop-off',
    statColor: '#f59e0b',
    icon: <TrendingDown className="w-5 h-5" />,
  },
  Arrears: {
    badge: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    button: 'bg-orange-500/15 border-orange-500/40 text-orange-400 hover:bg-orange-500/25 hover:border-orange-500/60',
    buttonLabel: 'VIEW IN ARREARS',
    route: '/reports/arrears',
    statColor: '#fb923c',
    icon: <AlertTriangle className="w-5 h-5" />,
  },
  Clawbacks: {
    badge: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    button: 'bg-rose-500/15 border-rose-500/40 text-rose-400 hover:bg-rose-500/25 hover:border-rose-500/60',
    buttonLabel: 'VIEW IN DISCHARGED LOANS',
    route: '/reports/discharged-loans',
    statColor: '#f43f5e',
    icon: <Undo2 className="w-5 h-5" />,
  },
  'Client Care': {
    badge: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    button: 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 hover:border-cyan-500/60',
    buttonLabel: 'VIEW IN CLIENT CARE',
    route: '/clients',
    statColor: '#22d3ee',
    icon: <Contact className="w-5 h-5" />,
  },
};

const FEED_SOURCES = Object.keys(SOURCE_CONFIG) as FeedSourcePage[];

const CHANNEL_ICON: Record<ContactChannel, { Icon: React.ComponentType<{ className?: string }>; color: string }> = {
  phone: { Icon: Phone, color: 'text-violet-400' },
  email: { Icon: Mail, color: 'text-amber-400' },
  sms: { Icon: MessageSquare, color: 'text-blue-400' },
  meeting: { Icon: Users, color: 'text-green-400' },
};
const CHANNEL_LABEL: Record<ContactChannel, string> = {
  phone: 'Phone',
  email: 'Email',
  sms: 'SMS',
  meeting: 'Meeting',
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(date: Date): string {
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) + ' ' +
    date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function formatFullTimestamp(date: Date): string {
  return date.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function toYYYYMMDD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDate(yyyy_mm_dd: string): Date {
  return new Date(yyyy_mm_dd + 'T00:00:00');
}

// Get the Monday of the week containing the given date
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day; // Adjust to Monday
  d.setDate(d.getDate() + diff);
  return d;
}

function getWeekEnd(weekStart: Date): Date {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  return d;
}

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getDateRange(selectedDate: string, viewMode: ViewMode): { start: Date; end: Date } {
  const date = parseDate(selectedDate);
  switch (viewMode) {
    case 'day':
      return { start: date, end: date };
    case 'week': {
      const ws = getWeekStart(date);
      return { start: ws, end: getWeekEnd(ws) };
    }
    case 'month': {
      return { start: getMonthStart(date), end: getMonthEnd(date) };
    }
  }
}

function isInRange(date: Date, start: Date, end: Date): boolean {
  const d = toYYYYMMDD(date);
  return d >= toYYYYMMDD(start) && d <= toYYYYMMDD(end);
}

function isSameMonth(date: Date, yyyy_mm_dd: string): boolean {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}` === yyyy_mm_dd.substring(0, 7);
}

function getPreviousMonth(yyyy_mm_dd: string): string {
  const d = parseDate(yyyy_mm_dd);
  d.setMonth(d.getMonth() - 1);
  return toYYYYMMDD(d);
}

function formatRangeLabel(selectedDate: string, viewMode: ViewMode): string {
  const { start, end } = getDateRange(selectedDate, viewMode);
  switch (viewMode) {
    case 'day':
      return start.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    case 'week': {
      const s = start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
      const e = end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      return `${s} to ${e}`;
    }
    case 'month':
      return start.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  }
}

function formatPeriodLabel(selectedDate: string, viewMode: ViewMode): string {
  switch (viewMode) {
    case 'day':
      return parseDate(selectedDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    case 'week':
      return 'This week';
    case 'month':
      return parseDate(selectedDate).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  }
}

const ContactActivityPage: React.FC = () => {
  const { userData } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  const { entries, loading, error } = useContactActivity(userData?.companyId);

  // View mode & date navigation
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [selectedDate, setSelectedDate] = useState(toYYYYMMDD(new Date()));

  // Filters
  const [staffFilter, setStaffFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | FeedSourcePage>('all');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');

  // Sort
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Date range for current view
  const dateRange = useMemo(() => getDateRange(selectedDate, viewMode), [selectedDate, viewMode]);

  // Range-filtered entries (used for table + Row 1 stat cards)
  const rangeEntries = useMemo(
    () => entries.filter((e) => isInRange(e.createdAt, dateRange.start, dateRange.end)),
    [entries, dateRange]
  );

  // Monthly entries for stat cards row 2
  const currentMonthEntries = useMemo(
    () => entries.filter((e) => isSameMonth(e.createdAt, selectedDate)),
    [entries, selectedDate]
  );
  const prevMonthEntries = useMemo(() => {
    const prevMonth = getPreviousMonth(selectedDate);
    return entries.filter((e) => isSameMonth(e.createdAt, prevMonth));
  }, [entries, selectedDate]);

  // Staff members for filter dropdown (from current range)
  const staffMembers = useMemo(() => {
    const names = new Set(rangeEntries.map((e) => e.createdBy).filter(Boolean));
    return Array.from(names).sort();
  }, [rangeEntries]);

  // Apply filters
  const filteredEntries = useMemo(() => {
    let result = rangeEntries;
    if (staffFilter !== 'all') {
      result = result.filter((e) => e.createdBy === staffFilter);
    }
    if (sourceFilter !== 'all') {
      result = result.filter((e) => e.sourcePage === sourceFilter);
    }
    if (channelFilter !== 'all') {
      result = result.filter((e) => e.channels.includes(channelFilter));
    }
    return result;
  }, [rangeEntries, staffFilter, sourceFilter, channelFilter]);

  // Sort
  const sortedEntries = useMemo(() => {
    const sorted = [...filteredEntries];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'createdAt':
          cmp = a.createdAt.getTime() - b.createdAt.getTime();
          break;
        case 'createdBy':
          cmp = (a.createdBy || '').localeCompare(b.createdBy || '');
          break;
        case 'broker':
          cmp = (a.broker || '').localeCompare(b.broker || '');
          break;
        case 'customerName':
          cmp = (a.customerName || '').localeCompare(b.customerName || '');
          break;
        case 'sourcePage':
          cmp = a.sourcePage.localeCompare(b.sourcePage);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredEntries, sortField, sortDirection]);

  // Pagination slice
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedEntries.slice(start, start + pageSize);
  }, [sortedEntries, currentPage, pageSize]);

  // Stat card computations — Row 1 (range totals)
  const rangeStats = useMemo(() => ({
    total: rangeEntries.length,
    uniqueCustomers: new Set(rangeEntries.map((e) => e.customerName)).size,
    phoneCalls: rangeEntries.filter((e) => e.channels.includes('phone')).length,
    emails: rangeEntries.filter((e) => e.channels.includes('email')).length,
  }), [rangeEntries]);

  // Stat card computations — Row 2 (Monthly by source with MoM), every feed source
  const monthlySourceStats = useMemo(() => {
    return FEED_SOURCES.map((src) => {
      const current = currentMonthEntries.filter((e) => e.sourcePage === src).length;
      const prev = prevMonthEntries.filter((e) => e.sourcePage === src).length;
      let momText = '';
      let momDirection: 'up' | 'down' | 'none' = 'none';
      if (prev === 0 && current > 0) {
        momText = 'No prior data';
      } else if (prev > 0) {
        const pct = Math.round(((current - prev) / prev) * 100);
        if (pct > 0) {
          momText = `+${pct}%`;
          momDirection = 'up';
        } else if (pct < 0) {
          momText = `${pct}%`;
          momDirection = 'down';
        } else {
          momText = '0%';
        }
      }
      return { sourcePage: src, count: current, momText, momDirection };
    });
  }, [currentMonthEntries, prevMonthEntries]);

  // Navigation handlers (step by view mode)
  const goToPrevious = () => {
    const d = parseDate(selectedDate);
    switch (viewMode) {
      case 'day': d.setDate(d.getDate() - 1); break;
      case 'week': d.setDate(d.getDate() - 7); break;
      case 'month': d.setMonth(d.getMonth() - 1); break;
    }
    setSelectedDate(toYYYYMMDD(d));
    resetFilters();
  };

  const goToNext = () => {
    const d = parseDate(selectedDate);
    switch (viewMode) {
      case 'day': d.setDate(d.getDate() + 1); break;
      case 'week': d.setDate(d.getDate() + 7); break;
      case 'month': d.setMonth(d.getMonth() + 1); break;
    }
    setSelectedDate(toYYYYMMDD(d));
    resetFilters();
  };

  const goToToday = () => {
    setSelectedDate(toYYYYMMDD(new Date()));
    resetFilters();
  };

  const resetFilters = () => {
    setStaffFilter('all');
    setSourceFilter('all');
    setChannelFilter('all');
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'createdAt' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Deep-link to the owning surface. Client Care is client-keyed; loan-based
  // sources carry one or more bare loan ids — link to the first.
  const handleDeepLink = (entry: ContactActivityEntry) => {
    const cfg = SOURCE_CONFIG[entry.sourcePage];
    if (entry.sourcePage === 'Client Care') {
      navigate(entry.clientId ? `${cfg.route}?clientId=${encodeURIComponent(entry.clientId)}` : cfg.route);
      return;
    }
    if (entry.loanIds.length > 0) {
      navigate(`${cfg.route}?loanId=${encodeURIComponent(entry.loanIds[0])}`);
      return;
    }
    navigate(cfg.route);
  };

  const referenceLabel = (entry: ContactActivityEntry): string => {
    if (entry.sourcePage === 'Client Care') return entry.customerName || entry.clientId || '—';
    return entry.loanIds.length > 0 ? entry.loanIds.join(', ') : '—';
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return <span className="ml-1 text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  const isToday = selectedDate === toYYYYMMDD(new Date());

  // Show date+time in table for week/month views, time-only for day
  const formatTimeColumn = viewMode === 'day' ? formatTime : formatDateTime;

  const periodLabel = formatPeriodLabel(selectedDate, viewMode);

  // Date picker + view mode toggle for PageHeader actions
  const dateActions = (
    <div className="flex items-center gap-2 flex-wrap">
      {/* View mode toggle */}
      <div className="flex rounded-lg border border-gray-700 overflow-hidden">
        {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => { setViewMode(mode); resetFilters(); }}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === mode
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {/* Date navigation */}
      <button
        onClick={goToPrevious}
        className="p-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
        title={`Previous ${viewMode}`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <input
        type="date"
        value={selectedDate}
        onChange={(e) => {
          setSelectedDate(e.target.value);
          resetFilters();
        }}
        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <button
        onClick={goToNext}
        className="p-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
        title={`Next ${viewMode}`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      <button
        onClick={goToToday}
        disabled={isToday}
        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Today
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ClipboardCheck}
        title="Contact Activity"
        subtitle={formatRangeLabel(selectedDate, viewMode)}
        actions={dateActions}
      />

      {/* Row 1: Range stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Contacts"
          value={rangeStats.total.toLocaleString()}
          icon={<Activity className="w-5 h-5" />}
          gradientColor="#06b6d4"
          periodLabel={periodLabel}
        />
        <StatCard
          title="Unique Customers"
          value={rangeStats.uniqueCustomers.toLocaleString()}
          icon={<Users className="w-5 h-5" />}
          gradientColor="#6366f1"
          periodLabel={periodLabel}
        />
        <StatCard
          title="Phone Contacts"
          value={rangeStats.phoneCalls.toLocaleString()}
          icon={<Phone className="w-5 h-5" />}
          gradientColor="#a855f7"
          periodLabel={periodLabel}
        />
        <StatCard
          title="Email Contacts"
          value={rangeStats.emails.toLocaleString()}
          icon={<Mail className="w-5 h-5" />}
          gradientColor="#ec4899"
          periodLabel={periodLabel}
        />
      </div>

      {/* Row 2: Monthly by source with MoM (every feed source) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {monthlySourceStats.map((stat) => (
          <StatCard
            key={stat.sourcePage}
            title={stat.sourcePage}
            value={stat.count.toLocaleString()}
            icon={SOURCE_CONFIG[stat.sourcePage].icon}
            gradientColor={SOURCE_CONFIG[stat.sourcePage].statColor}
            periodLabel="This month"
            subtitle={
              stat.momDirection === 'up'
                ? `${stat.momText} vs last month`
                : stat.momDirection === 'down'
                ? `${stat.momText} vs last month`
                : stat.momText || undefined
            }
          />
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={staffFilter}
          onChange={(e) => { setStaffFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">All Staff</option>
          {staffMembers.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value as any); setCurrentPage(1); }}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">All Sources</option>
          {FEED_SOURCES.map((src) => (
            <option key={src} value={src}>{src}</option>
          ))}
        </select>

        <select
          value={channelFilter}
          onChange={(e) => { setChannelFilter(e.target.value as ChannelFilter); setCurrentPage(1); }}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">All Channels</option>
          {(Object.keys(CHANNEL_LABEL) as ContactChannel[]).map((ch) => (
            <option key={ch} value={ch}>{CHANNEL_LABEL[ch]}</option>
          ))}
        </select>

        {(staffFilter !== 'all' || sourceFilter !== 'all' || channelFilter !== 'all') && (
          <button
            onClick={resetFilters}
            className="px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-300 hover:text-white hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" label="Loading contact activity..." />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {sortedEntries.length === 0 ? (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
              <ClipboardCheck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                No contact activity recorded for {formatRangeLabel(selectedDate, viewMode)}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Contacts logged from any report (Discharges, Client Retention, Acquired Books,
                Settlement Age, Trail Drop-Off, Arrears) and Client Care appear here.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-800/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('createdAt')}>
                          {viewMode === 'day' ? 'Time' : 'Date / Time'} {getSortIndicator('createdAt')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('createdBy')}>
                          Logged By {getSortIndicator('createdBy')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('broker')}>
                          Contact Made By {getSortIndicator('broker')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('customerName')}>
                          Customer {getSortIndicator('customerName')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Channels
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Outcome
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => handleSort('sourcePage')}>
                          Source {getSortIndicator('sourcePage')}
                        </th>
                        <th className="px-4 py-3 w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {paginatedEntries.map((entry) => {
                        const isExpanded = expandedRows.has(entry.id);
                        return (
                          <React.Fragment key={entry.id}>
                            <tr
                              className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                              onClick={() => toggleRow(entry.id)}
                            >
                              <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                                {formatTimeColumn(entry.createdAt)}
                              </td>
                              <td className="px-4 py-3 text-white whitespace-nowrap">
                                {entry.createdBy || '—'}
                              </td>
                              <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                                {entry.broker || '—'}
                              </td>
                              <td className="px-4 py-3 text-white max-w-[200px] truncate">
                                {entry.customerName}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1.5">
                                  {entry.channels.length > 0 ? (
                                    entry.channels.map((ch) => {
                                      const { Icon, color } = CHANNEL_ICON[ch];
                                      return <Icon key={ch} className={`w-4 h-4 ${color}`} />;
                                    })
                                  ) : (
                                    <span className="text-gray-500">—</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                                {entry.outcome ? (CONTACT_OUTCOMES[entry.outcome] || entry.outcome) : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${SOURCE_CONFIG[entry.sourcePage].badge}`}>
                                  {entry.sourcePage}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-400">
                                <ChevronDown
                                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-gray-900/60">
                                <td colSpan={8} className="px-4 py-0">
                                  <div className="border-l-2 border-gray-600 ml-2 pl-6 py-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                                      {/* Column 1: Comments + contact date + outcome */}
                                      <div className="space-y-4">
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Comments</p>
                                          <p className="text-white whitespace-pre-wrap">{entry.comments || '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Contact Date</p>
                                          <p className="text-green-400">{entry.date || 'Not recorded'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Channels</p>
                                          <p className="text-white">
                                            {entry.channels.length > 0
                                              ? entry.channels.map((ch) => CHANNEL_LABEL[ch]).join(', ')
                                              : '—'}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Column 2: actor (logged by) + made-by + full timestamp */}
                                      <div className="space-y-4">
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Logged By</p>
                                          <p className="text-white font-medium">{entry.createdBy || '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Contact Made By</p>
                                          <p className="text-white font-medium">{entry.broker || '—'}</p>
                                        </div>
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Logged At</p>
                                          <p className="text-gray-300 font-mono text-xs">{formatFullTimestamp(entry.createdAt)}</p>
                                        </div>
                                      </div>

                                      {/* Column 3: reference + deep-link button */}
                                      <div className="space-y-3">
                                        <div>
                                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">
                                            {entry.sourcePage === 'Client Care' ? 'Client' : 'Loan ID'}
                                          </p>
                                          <div className="flex items-center gap-3 flex-wrap">
                                            <span className={entry.sourcePage === 'Client Care' ? 'text-white font-medium' : 'text-white font-mono font-medium'}>
                                              {referenceLabel(entry)}
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeepLink(entry);
                                              }}
                                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded border text-[11px] font-medium transition-colors uppercase tracking-wider ${SOURCE_CONFIG[entry.sourcePage].button}`}
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              {SOURCE_CONFIG[entry.sourcePage].buttonLabel}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <Pagination
                currentPage={currentPage}
                totalItems={sortedEntries.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ContactActivityPage;
